import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBranches, changeBranchState, deleteBranch } from '../services/branchService';
import BranchForm from '../components/BranchForm';

export default function Sucursales() {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const loadBranches = useCallback(() => {
    setLoading(true);
    setError('');
    return getBranches()
      .then(data => setBranches(Array.isArray(data) ? data : []))
      .catch(() => setError('No se pudo cargar el listado de sucursales.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  function openCreateForm() {
    setEditingBranch(null);
    setShowForm(true);
  }

  function openEditForm(branch) {
    setEditingBranch(branch);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingBranch(null);
  }

  function handleSaved() {
    setShowForm(false);
    setEditingBranch(null);
    loadBranches();
  }

  async function toggleState(branch) {
    setActionError('');
    setBusy(true);
    try {
      await changeBranchState(branch.id);
      await loadBranches();
    } catch (err) {
      setActionError(err.response?.data?.msg || 'No se pudo cambiar el estado de la sucursal.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setActionError('');
    setBusy(true);
    try {
      await deleteBranch(pendingDelete.id);
      setPendingDelete(null);
      await loadBranches();
    } catch (err) {
      setActionError(err.response?.data?.msg || 'No se pudo eliminar la sucursal.');
      setPendingDelete(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
            Administración de sucursales y depósitos
          </p>
          <h5 style={{
            fontWeight: 800, color: 'var(--orig-text)',
            fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
          }}>
            Sucursales
          </h5>
        </div>
        {!showForm && (
          <button className="btn-orig btn-orig-green" onClick={openCreateForm}>
            <i className="bi bi-plus-lg" />
            Nueva Sucursal
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ marginBottom: 20 }}>
          <BranchForm
            branch={editingBranch}
            onCancel={closeForm}
            onSaved={handleSaved}
          />
        </div>
      )}

      {/* Results */}
      <div className="section-card">
        <div className="section-header">
          <h6>
            <i className="bi bi-list-ul" style={{ color: 'var(--orig-green)' }} />
            Resultados
          </h6>
          {!loading && (
            <span className="badge-orig badge-green">
              {branches.length} {branches.length === 1 ? 'sucursal' : 'sucursales'}
            </span>
          )}
        </div>

        {actionError && (
          <div style={{ padding: '14px 20px 0' }}>
            <div className="alert-orig alert-error">
              <i className="bi bi-exclamation-triangle-fill" />
              {actionError}
            </div>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-arrow-repeat" style={{
                fontSize: 28, display: 'block', marginBottom: 10,
                animation: 'spin 1s linear infinite',
              }} />
              Cargando sucursales...
            </div>
          ) : error ? (
            <div style={{ padding: '28px 20px' }}>
              <div className="alert-orig alert-error">
                <i className="bi bi-exclamation-triangle-fill" />
                {error}
              </div>
            </div>
          ) : branches.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-building" style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sin resultados</div>
              <div style={{ fontSize: 12 }}>No hay sucursales registradas.</div>
            </div>
          ) : (
            <table className="orig-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Depósitos</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {branches.map(b => (
                  <React.Fragment key={b.id}>
                    <tr>
                      <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                        {b.id}
                      </td>
                      <td style={{ fontWeight: 500 }}>{b.name}</td>
                      <td>
                        {b.deposits?.length ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {b.deposits.map(d => (
                              <span
                                key={d.id}
                                title={d.state ? 'Activo' : 'Inactivo'}
                                style={{
                                  background: d.main ? 'var(--orig-green-dim)' : 'var(--orig-teal-dim)',
                                  color: d.main ? 'var(--orig-green-bright)' : 'var(--orig-teal-light)',
                                  padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                  opacity: d.state ? 1 : 0.5,
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                }}
                              >
                                {d.main && <i className="bi bi-star-fill" style={{ fontSize: 9 }} />}
                                {d.name}
                              </span>
                            ))}
                          </div>
                        ) : <span style={{ color: 'var(--orig-text-hint)' }}>—</span>}
                      </td>
                      <td>
                        <span className={`badge-orig ${b.state ? 'badge-green' : 'badge-red'}`}>
                          <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
                          {b.state ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            className="btn-orig btn-orig-outline"
                            onClick={() => navigate(`/sucursales/${b.id}/inventario`)}
                            style={{ padding: '5px 12px', fontSize: 12 }}
                            title="Ver inventario"
                          >
                            <i className="bi bi-eye" />
                          </button>
                          <button
                            className="btn-orig btn-orig-outline"
                            onClick={() => openEditForm(b)}
                            style={{ padding: '5px 12px', fontSize: 12 }}
                            title="Editar sucursal"
                          >
                            <i className="bi bi-pencil" />
                            Editar
                          </button>
                          <button
                            className="btn-orig btn-orig-outline"
                            onClick={() => toggleState(b)}
                            disabled={busy}
                            style={{ padding: '5px 12px', fontSize: 12 }}
                            title="Cambiar estado"
                          >
                            <i className="bi bi-toggle2-on" />
                          </button>
                          <button
                            className="btn-orig btn-orig-outline"
                            onClick={() => setPendingDelete(b)}
                            disabled={busy}
                            style={{ padding: '5px 12px', fontSize: 12, color: 'var(--orig-red-light)' }}
                            title="Eliminar sucursal"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {pendingDelete?.id === b.id && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0 }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                            background: 'var(--orig-red-dim)',
                            padding: '10px 14px',
                          }}>
                            <span style={{ fontSize: 13, color: 'var(--orig-text)' }}>
                              ¿Eliminar la sucursal <strong>{pendingDelete.name}</strong>?
                            </span>
                            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                              <button type="button" className="btn-orig btn-orig-green" onClick={confirmDelete} disabled={busy}>
                                Sí, eliminar
                              </button>
                              <button type="button" className="btn-orig btn-orig-outline" onClick={() => setPendingDelete(null)} disabled={busy}>
                                No
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
